import React, { useState, useMemo } from "react";
import type {
  GroupMemberBeneficiary,
  EnhancedSummaryStepProps,
} from "../../../types/customer-portal";
import AnimatedChevron from "../../../../../components/common/AnimatedChevron";
import Pagination from "../../../../../shared/components/ui/Pagination/Pagination";
import { usePlanSelection } from "../../../contexts/PlanSelectionContext";
import { useUpdatedPremiumPricebyAge } from "../../../hooks/useUpdatedPremiumPricebyAge";
import { useTransaction } from "../../../hooks/useTransaction";
import { formatAddressForSummary } from "../../../utils/addressFormatter";
import { formatFullName } from "../../../utils/nameFormatter";
import { dateUtils } from "../../../../../shared/utils/dateUtils";
import "./SummaryStep.css";
import RecaptchaModal from "@/portals/customer/modals/RecaptchaModal";
import PaymentTransactionModal from "@/portals/customer/modals/PaymentTransactionModal";
import { useAppStore } from "@/store/useAppStore";
import { formatWithCommas } from "@/shared/utils/numberUtils";
import { addDays, format } from "date-fns";
import { STORAGE_KEYS } from "@/portals/customer/utils/sessionStateManager";

interface InsuredPerson {
  id: string;
  type: string;
  title: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  dateOfBirth: string;
  age: string;
  gender?: string;
  occupation?: string;
  nationality: string;
  civilStatus?: string;
  address: string;
  contactNumber?: string;
  idType?: string;
  idNumber?: string;
  beneficiaries?: GroupMemberBeneficiary[];
}

export interface TravelDetailsProps {
  payment_link?: string;
  coverage_start?: string;
  coverage_end?: string;
  itinerary?: string;
  expiry_date?: string;
}

const SummaryStep: React.FC<EnhancedSummaryStepProps> = ({
  data,
  planData,
  travelData,
  planSelection,
  travelDetailsData,
  premiumAdjustments: propPremiumAdjustments,
  onEditDetails,
  utmData
}) => {

  const { payment } = useAppStore();
  const formattePaymentPremiumPrice = formatWithCommas(
    payment.adjustedPriceByAge?.rateAmount || 0,
  );
  const paymentPremiumAmountStr = `${payment.adjustedPriceByAge?.currencyCode} ${formattePaymentPremiumPrice}`;
  const customerPortalSessionState = sessionStorage.getItem(
    STORAGE_KEYS.SESSION_STATE,
  );

  // Access plan selection context
  const { selectedPlan, premiumAdjustments: contextPremiumAdjustments, getTempDataValue, tempData, getPrincipalId, principalData } = usePlanSelection();
  
  // Access premium adjustment data (keep for backward compatibility but prioritize context)
  const { adjustedPrice } = useUpdatedPremiumPricebyAge();
  
  // ENHANCED: Get tempData values including forex conversion data
  const tempDataRateAmount = getTempDataValue('rateAmount') as number | null;
  const tempDataCurrencyCode = getTempDataValue('currencyCode') as string | null;
  const tempDataProductName = getTempDataValue('productName') as string | null;
  
  // Get forex conversion data
  const isForexConverted = getTempDataValue("isForexConverted") as boolean;
  const convertedCurrencyCode = getTempDataValue("convertedCurrencyCode") as
    | string
    | null;
  const convertedRateAmount = getTempDataValue("convertedRateAmount") as
    | number
    | null;
  const originalCurrencyCode = getTempDataValue("originalCurrencyCode") as
    | string
    | null;
  const originalRateAmount = getTempDataValue("originalRateAmount") as
    | number
    | null;


  const originalPremiumAmountStr =
    payment.isForexConverted && payment.converted?.rateAmount != null
      ? `${payment.converted.currencyCode} ${formatWithCommas(payment.converted.rateAmount)}`
      : isForexConverted && originalRateAmount != null && originalCurrencyCode
        ? `${originalCurrencyCode} ${formatWithCommas(originalRateAmount)}`
        : `${payment.currencyCode} ${formatWithCommas(payment.rateAmount)}`;

  // Enhanced debugging for tempData retrieval issues
  console.log('SummaryStep: Complete tempData analysis:', {
    allTempDataKeys: tempData ? Object.keys(tempData as Record<string, unknown>) : [],
    tempDataType: typeof tempData,
    tempDataExists: !!tempData,
    criticalFields: {
      rateAmount: getTempDataValue('rateAmount'),
      currencyCode: getTempDataValue('currencyCode'),
      productName: getTempDataValue('productName'),
      typeName: getTempDataValue('typeName'),
      planType: getTempDataValue('planType')
    }
  });
  
  // Use the generic transaction hook
  const {
    createTransaction,
    loading: transactionLoading,
    error: transactionError,
    clearState: clearTransactionState
  } = useTransaction();

  // State hooks - must come after all other hooks
  const [expandedSections, setExpandedSections] = useState<{
    travelDetails: boolean;
    [key: string]: boolean; // Allow dynamic keys for insured persons
  }>({
    travelDetails: false
  });

  // Pagination state for Insured Person/s section
  const [insuredPersonsPagination, setInsuredPersonsPagination] = useState({
    currentPage: 1,
    itemsPerPage: 5
  });

  const [termsAccepted, setTermsAccepted] = useState({
    privacyStatement: false,
    applicationTerms: false,
    marketingConsent: false
  });

  const [showRecaptchaModal, setShowRecaptchaModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState<{
    isOpen: boolean;
    type: 'pending' | 'overlap';
    travelDetails: TravelDetailsProps | null;
  }>({ isOpen: false, type: 'pending', travelDetails: null });

  // Handle link clicks for Terms and Conditions
  const handleLinkClick = (linkType: string) => {
    const baseUrl = (import.meta.env.VITE_APP_BASE_URL || "/travel-portal").replace(/\/?$/, "/");

    switch (linkType) {
      case 'privacy-statement':
        // Route to existing Privacy Statement Section
        window.open(
          `${baseUrl}customer-portal/privacy-statement`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
      case 'application-terms':
        // Open Application Terms and Conditions PDF
        window.open(
          `${baseUrl}pdfs/MyT_App_Terms_and_Conditions_2024-08.pdf`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
      case 'exclusions':
        // Open Exclusions PDF
        window.open(
          `${baseUrl}pdfs/MyT_Exclusions_2024-08.pdf`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
      case 'important-notes':
        // Open Important Notes PDF
        window.open(
          `${baseUrl}pdfs/MyT_Imp_Notes_for_Family_Plans_2024-08.pdf`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
      case 'terms-defined':
        // Open Some Terms Defined and Other Important Details PDF
        window.open(
          `${baseUrl}pdfs/MyT_Some_Terms_Defined_2024-08.pdf`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
      case 'payment-terms':
        // Open Payment Terms and Conditions PDF
        window.open(
          `${baseUrl}pdfs/MyT_App_Terms_and_Conditions_2024-08.pdf`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
      default:
        console.warn('Unknown link type:', linkType);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle pagination for Insured Person/s section
  const handleInsuredPersonsPageChange = (newPage: number) => {
    setInsuredPersonsPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
    
    // Reset expanded sections when changing pages
    setExpandedSections(prev => {
      const newExpanded = { travelDetails: prev.travelDetails };
      return newExpanded;
    });
  };

  // Calculate pagination data for insured persons
  const calculatePaginationData = (insuredPersons: InsuredPerson[]) => {
    const { currentPage, itemsPerPage } = insuredPersonsPagination;
    const totalItems = insuredPersons.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPageItems = insuredPersons.slice(startIndex, endIndex);
    
    return {
      currentPageItems,
      totalPages,
      totalItems,
      currentPage,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1
    };
  };

  const handleTermsChange = (term: keyof typeof termsAccepted) => {
    setTermsAccepted(prev => ({
      ...prev,
      [term]: !prev[term]
    }));
  };

  const handleEditDetails = () => {
    onEditDetails();
  };

  const handlePay = async () => {
    console.log('Pay button clicked - proceeding directly to payment');
    
    try {
      // Clear any previous transaction state
      clearTransactionState();
      
      // Get principal ID from dedicated context (priority) or fallback to tempData
      const principalId = getPrincipalId();
      
      // CRITICAL FIX: Enhanced email retrieval with multiple fallback mechanisms
      const getEmailWithFallbacks = (): string | undefined => {
        // Priority 1: Get from tempData context (HIGHEST PRIORITY - newly added)
        const contextEmail = getTempDataValue('email') as string | undefined;
        if (contextEmail && contextEmail.trim() !== '') {
          console.log('SummaryStep: Using email from tempData context:', contextEmail);
          return contextEmail;
        }
        
        // Priority 2: Get from principalData context
        if (principalData?.email && principalData.email.trim() !== '') {
          console.log('SummaryStep: Using email from principalData context:', principalData.email);
          return principalData.email;
        }
        
        // Priority 3: Get from localStorage email verification data
        try {
          const localEmailVerificationData = localStorage.getItem('emailVerificationTempData');
          if (localEmailVerificationData) {
            const parsedData = JSON.parse(localEmailVerificationData);
            const localEmail = parsedData.email || parsedData.emailAddress;
            if (localEmail && localEmail.trim() !== '') {
              console.log('SummaryStep: Using email from localStorage emailVerificationTempData:', localEmail);
              return localEmail;
            }
          }
        } catch (error) {
          console.warn('SummaryStep: Error parsing localStorage emailVerificationTempData:', error);
        }
        
        // Priority 4: Get from sessionStorage email verification data
        try {
          const sessionEmailVerificationData = sessionStorage.getItem('emailVerificationTempData');
          if (sessionEmailVerificationData) {
            const parsedData = JSON.parse(sessionEmailVerificationData);
            const sessionEmail = parsedData.email || parsedData.emailAddress;
            if (sessionEmail && sessionEmail.trim() !== '') {
              console.log('SummaryStep: Using email from sessionStorage emailVerificationTempData:', sessionEmail);
              return sessionEmail;
            }
          }
        } catch (error) {
          console.warn('SummaryStep: Error parsing sessionStorage emailVerificationTempData:', error);
        }
        
        // Priority 5: Get from contact information step data
        if (data.contactinformation && 'email' in data.contactinformation) {
          const contactEmail = data.contactinformation.email as string;
          if (contactEmail && contactEmail.trim() !== '') {
            console.log('SummaryStep: Using email from contactinformation step:', contactEmail);
            return contactEmail;
          }
        }
        
        console.warn('SummaryStep: No email found in any fallback mechanism');
        return undefined;
      };
      
      // Get email with comprehensive fallback logic
      const emailWithFallbacks = getEmailWithFallbacks();
      
      // Construct tempData from context for the transaction hook
      const tempData = {
        productName: getTempDataValue('productName') as string | undefined,
        typeName: getTempDataValue('typeName') as string | undefined,
        rateAmount: payment.rateAmount,
        currencyCode: payment.currencyCode,
        currencyId: payment.currencyId,
        startTrip: getTempDataValue('startTrip') as string | undefined,
        destinations: getTempDataValue('destinations') as string | string[] | undefined,
        endTrip: getTempDataValue('endTrip') as string | undefined,
        startDate: getTempDataValue('startDate') as string | undefined,
        endDate: getTempDataValue('endDate') as string | undefined,
        numberOfTraveler: getTempDataValue('numberOfTraveler') as number | undefined,
        numberOfTravelers: getTempDataValue('numberOfTravelers') as number | undefined,
        travelPurpose: getTempDataValue('travelPurpose') as string | undefined,
        purposeOfTravel: getTempDataValue('purposeOfTravel') as string | undefined,
        // Use principal ID from dedicated context with fallback to tempData
        principal_id: principalId || (getTempDataValue('principal_id') as number | undefined),
        principalId: principalId || (getTempDataValue('principalId') as number | undefined),
        middleName: getTempDataValue('middleName') as string | undefined,
        suffix: getTempDataValue('suffix') as string | undefined,
        birthDate: getTempDataValue('birthDate') as string | undefined,
        dateOfBirth: getTempDataValue('dateOfBirth') as string | undefined,
        // CRITICAL FIX: Use enhanced email retrieval with fallbacks
        email: emailWithFallbacks,
        mbl: getTempDataValue('mbl') as string | number | undefined
      };
      
      console.log('SummaryStep: Retrieved principal ID from context:', principalId);
      console.log('SummaryStep: Constructed tempData for transaction:', tempData);
      
      // Create the transaction with enhanced plan data (automatically determines type: Individual, Family, Group, Multi Trip)
      const response = await createTransaction(
        data,
        selectedPlan,
        travelDetailsData,
        planSelection,
        tempData, // Pass the constructed tempData from context
        enhancedPlanData, // Pass the enhanced plan data from SummaryStep
        getPrincipalId, // Pass the context's getPrincipalId function for Group Plans
        utmData
      );

      if (response.hasPendingError) {
        setPaymentModal({ isOpen: true, type: 'pending', travelDetails: response.transactionDetails ?? null });
      } else if (response.hasOverlappingError) {
        setPaymentModal({ isOpen: true, type: 'overlap', travelDetails: response.transactionDetails ?? null });
      } else if (response.success && response.paymentLink) {
        window.open(response.paymentLink, "_self");
      } else {
        console.error("Transaction failed:", response.message);
      }
    } catch (error) {
      console.error('Error during transaction creation:', error);
      // Error handling is already managed by the hook
    }
  };

  // Check if all required terms are accepted (marketingConsent is optional)
  const allTermsAccepted = termsAccepted.privacyStatement && termsAccepted.applicationTerms;

  // ENHANCED: Helper functions to extract data from tempData with forex conversion support
  const getTempDataPlanDetails = useMemo(() => {
    // Prioritize converted currency data if forex conversion occurred
    const effectiveCurrencyCode = isForexConverted && convertedCurrencyCode ? convertedCurrencyCode : tempDataCurrencyCode;
    const effectiveRateAmount = isForexConverted && convertedRateAmount ? convertedRateAmount : tempDataRateAmount;
    
    return {
      productName: tempDataProductName,
      typeName: getTempDataValue('typeName') as string | null,
      rateAmount: effectiveRateAmount,
      currencyCode: effectiveCurrencyCode,
      // Include forex conversion metadata for reference
      isForexConverted,
      originalCurrencyCode: isForexConverted ? originalCurrencyCode : null,
      originalRateAmount: isForexConverted ? originalRateAmount : null
    };
  }, [tempDataProductName, tempDataRateAmount, tempDataCurrencyCode, isForexConverted, convertedCurrencyCode, convertedRateAmount, originalCurrencyCode, originalRateAmount, getTempDataValue]);

  const getTempDataTravelDetails = useMemo(() => {
    const startTrip = getTempDataValue('startTrip') as string | null;
    const destinations = getTempDataValue('destinations') as string | string[] | null;
    const endTrip = getTempDataValue('endTrip') as string | null;
    const startDate = getTempDataValue('startDate') as string | null;
    const endDate = getTempDataValue('endDate') as string | null;
    
    return {
      startTrip: startTrip && startTrip.trim() !== '' ? startTrip : null,
      destinations: destinations,
      endTrip: endTrip && endTrip.trim() !== '' ? endTrip : null,
      startDate: startDate && startDate.trim() !== '' ? startDate : null,
      endDate: endDate && endDate.trim() !== '' ? endDate : null
    };
  }, [getTempDataValue]);

  // Enhanced coverage area mapping function with improved pattern matching
  const getCoverageArea = useMemo(() => {
    return (section?: string): string => {
      console.log('SummaryStep: getCoverageArea called with section:', section);
      if (!section) {
        console.log('SummaryStep: No section provided, defaulting to Domestic');
        return 'Domestic';
      }
      const normalizedSection = section.toLowerCase().trim();
      console.log('SummaryStep: Normalized section:', normalizedSection);
      
      // Enhanced matching for Worldwide Elite variations (including product names)
      if (normalizedSection.includes('worldwide elite dollar') ||
          normalizedSection.includes('worldwide elite') ||
          normalizedSection === 'worldwideelite' ||
          normalizedSection.includes('schengen')) {
        console.log('SummaryStep: Matched Worldwide Elite');
        return 'Worldwide Elite';
      }
      
      // Enhanced matching for Asia and Oceania variations
      if ((normalizedSection.includes('asia') && normalizedSection.includes('oceania')) ||
          normalizedSection === 'asia_oceania' ||
          normalizedSection === 'asia_and_oceania' ||
          normalizedSection === 'asiaandoceania' ||
          normalizedSection.includes('asia & oceania')) {
        console.log('SummaryStep: Matched Asia and Oceania');
        return 'Asia and Oceania';
      }
      
      // Enhanced matching for Short Secure variations
      if (normalizedSection.includes('short secure') ||
          normalizedSection === 'shortsecure' ||
          normalizedSection === 'short_secure') {
        console.log('SummaryStep: Matched Short Secure');
        return 'Short Secure';
      }
      
      // Enhanced matching for Domestic variations
      if (normalizedSection.includes('domestic')) {
        console.log('SummaryStep: Matched Domestic');
        return 'Domestic';
      }
      
      console.log('SummaryStep: No match found, defaulting to Domestic for section:', section);
      return 'Domestic';
    };
  }, []);

  // Format travel dates from travel details data
  const getFormattedTravelDates = useMemo(() => {
    if(customerPortalSessionState){
        const parsedCustomerPortalSessionState = JSON.parse(customerPortalSessionState);
        const travelFormData = parsedCustomerPortalSessionState?.travelFormData;
        if(travelFormData){
          if(travelFormData?.planType === "Multi Trip"){
          return format(travelFormData.travelDate, 'MMM dd, yyyy') + " - " + format(addDays(new Date(travelFormData.travelDate), 364), 'MMM dd, yyyy');
          }else{
            const formattedDates = format(travelFormData.startDate, 'MMM dd, yyyy') + " - " + format(travelFormData.endDate, 'MMM dd, yyyy')
            return formattedDates;
          }
        }
    }
    console.log('SummaryStep: getFormattedTravelDates called');
    console.log('SummaryStep: travelDetailsData:', travelDetailsData);
    console.log('SummaryStep: travelData:', travelData);
    
    if (travelDetailsData) {
      console.log('SummaryStep: Using travelDetailsData for dates');
      if (travelDetailsData.tripType === 'Annual Multi-Trip') {
        console.log('SummaryStep: Annual Multi-Trip, using travelDate:', travelDetailsData.travelDate);
        return travelDetailsData.travelDate;
      } else if (travelDetailsData.startDate && travelDetailsData.endDate) {
        const formattedDates = format(travelDetailsData.startDate, 'MMM dd, yyyy') + " - " + format(travelDetailsData.endDate, 'MMM dd, yyyy')
        console.log('SummaryStep: Single Trip, using formatted coverage period:', formattedDates);
        return formattedDates;
      }
    }
    console.log('SummaryStep: Falling back to travelData.coveragePeriod:', travelData?.coveragePeriod);
    return travelData?.coveragePeriod || 'Not specified';
  }, [travelDetailsData, travelData]);

  // Generate complete itinerary
  const getCompleteItinerary = useMemo(() => {
    if (travelDetailsData) {
      if (travelDetailsData.tripType === 'Annual Multi-Trip') {
        return 'Annual Multi-Trip Coverage';
      } else if (travelDetailsData.completeItinerary) {
        return travelDetailsData.completeItinerary;
      }
    }
    return travelData?.itinerary || 'Not specified';
  }, [travelDetailsData, travelData]);

  // Enhanced travel data with real data integration and tempData fallback
  const enhancedTravelData = useMemo(() => {
    console.log('SummaryStep: Creating enhancedTravelData');
    console.log('SummaryStep: planSelection:', planSelection);
    console.log('SummaryStep: planSelection?.section:', planSelection?.section);
    console.log('SummaryStep: travelDetailsData:', travelDetailsData);
    console.log('SummaryStep: tempData travel details:', getTempDataTravelDetails);
    
    const tempTravelDetails = getTempDataTravelDetails;
    
    // Generate itinerary - prioritize existing data, use tempData only if no valid existing data
    let itinerary = getCompleteItinerary;
    if ((!itinerary || itinerary === 'Not specified' || itinerary === '--') && tempTravelDetails?.startTrip && tempTravelDetails?.endTrip) {
      let destinationsStr = '';
      if (tempTravelDetails.destinations) {
        if (typeof tempTravelDetails.destinations === 'string') {
          destinationsStr = tempTravelDetails.destinations;
        } else if (Array.isArray(tempTravelDetails.destinations)) {
          destinationsStr = tempTravelDetails.destinations.join(', ');
        }
      }
      
      itinerary = destinationsStr
        ? `${tempTravelDetails.startTrip} - ${destinationsStr} - ${tempTravelDetails.endTrip}`
        : `${tempTravelDetails.startTrip} - ${tempTravelDetails.endTrip}`;
      console.log('SummaryStep: Using tempData for itinerary:', itinerary);
    }
    
    // FIXED: Enhanced coverage area logic with proper priority for both user flows
    console.log('SummaryStep: Coverage area logic - planSelection?.section:', planSelection?.section);
    console.log('SummaryStep: Coverage area logic - tempDataProductName:', tempDataProductName);
    console.log('SummaryStep: Coverage area logic - selectedPlan?.productName:', selectedPlan?.productName);
    console.log('SummaryStep: Coverage area logic - tempTravelDetails:', tempTravelDetails);
    
    let coverageArea = 'Domestic'; // Default fallback
    
    // Priority 1: Use selectedPlan.productName (most reliable for new users)
    if (selectedPlan?.productName) {
      coverageArea = getCoverageArea(selectedPlan.productName);
      console.log('SummaryStep: Using selectedPlan productName for coverage area:', coverageArea);
    }
    // Priority 2: Use tempDataProductName (for existing users with complete tempData)
    else if (tempDataProductName && tempDataProductName !== 'Domestic Bronze') {
      coverageArea = getCoverageArea(tempDataProductName);
      console.log('SummaryStep: Using tempData productName for coverage area:', coverageArea);
    }
    // Priority 3: Use planSelection.section as final fallback
    else if (planSelection?.section) {
      coverageArea = getCoverageArea(planSelection.section);
      console.log('SummaryStep: Using planSelection section for coverage area:', coverageArea);
    }
    
    console.log('SummaryStep: Final coverage area determined:', coverageArea);
    
    // Generate coverage period - prioritize existing data, use tempData as fallback with proper formatting
    let coveragePeriod = getFormattedTravelDates;
    if ((!coveragePeriod || coveragePeriod === 'Not specified') && tempTravelDetails?.startDate && tempTravelDetails?.endDate) {
      // Use dateUtils.formatCoveragePeriodRange for consistent formatting
      coveragePeriod = format(tempTravelDetails.startDate, 'MMM dd, yyyy') + " - " + format(tempTravelDetails.endDate, 'MMM dd, yyyy')
      console.log('SummaryStep: Using tempData for coverage period with proper formatting:', coveragePeriod);
    }
    
    const result = {
      itinerary: itinerary || 'Not specified',
      coverageArea: coverageArea || 'Domestic',
      coveragePeriod: coveragePeriod || 'Not specified'
    };
    
    console.log('SummaryStep: enhancedTravelData result:', result);
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCompleteItinerary, getCoverageArea, planSelection?.section, getFormattedTravelDates, getTempDataTravelDetails, getTempDataPlanDetails]);

  // Helper function to check if plan is Worldwide Elite
  const isWorldwideElitePlan = useMemo(() => {
    // Reuse existing logic for consistency
    const coverageArea = enhancedTravelData.coverageArea;
    return coverageArea === 'Worldwide Elite';
  }, [enhancedTravelData.coverageArea]);

  const principalInsured = data.principalinsured;
  const beneficiaries = data.beneficiarydetails?.beneficiaries || [];
  const familyMembers = data.familyinformation?.familyMembers || [];
  const personalAccidentBeneficiaries = data.personalaccidentbeneficiary?.beneficiaries || [];
  
  // Extract family information (spouse and children)
  const spouse = data.familyinformation?.spouse;
  const children = data.familyinformation?.children || [];

  // Determine plan type based on data
  const groupMembers = data.groupinformation?.groupMembers || [];
  const isGroupPlan = groupMembers.length > 0;
  const isFamilyPlan = spouse || children.length > 0;
  
  // Calculate premium data with real plan information
  const enhancedPlanData = useMemo(() => {
    // Determine plan type from travel data or infer from data structure
    let planType = 'Individual';
    if (travelDetailsData?.planType) {
      planType = travelDetailsData.planType;
    } else if (isGroupPlan) {
      planType = 'Group';
    } else if (isFamilyPlan) {
      planType = 'Family';
    }

    // Get tempData plan details
    const tempPlanDetails = getTempDataPlanDetails;

    // Calculate original premium and currency code with enhanced fallback logic and validation
    let originalPremiumAmount: number | null = null;
    let currencyCode: string | null = null;
    
    // ENHANCED: Priority logic with forex conversion support and improved new customer handling
    // Priority 1: Use converted currency data if forex conversion occurred
    if (isForexConverted && convertedRateAmount && convertedRateAmount > 0 &&
        convertedCurrencyCode && convertedCurrencyCode.trim() !== '') {
      originalPremiumAmount = convertedRateAmount;
      currencyCode = convertedCurrencyCode;
      console.log('SummaryStep: Using converted currency data:', tempDataRateAmount, {
        convertedAmount: tempDataRateAmount ||originalRateAmount,
        convertedCurrency: currencyCode,
        originalAmount: tempDataRateAmount ||originalRateAmount,
        originalCurrency: originalCurrencyCode
      });
    }
    // Priority 2: Use selectedPlan data (prioritized for new customers)
    else if (selectedPlan?.rate?.rateAmount &&
             typeof selectedPlan.rate.rateAmount === 'number' &&
             selectedPlan.rate.rateAmount > 0 &&
             !isNaN(selectedPlan.rate.rateAmount) &&
             selectedPlan?.rate?.currency?.currencyCode &&
             typeof selectedPlan.rate.currency.currencyCode === 'string' &&
             selectedPlan.rate.currency.currencyCode.trim() !== '') {
      originalPremiumAmount = tempDataRateAmount ? tempDataRateAmount: selectedPlan.rate.rateAmount;
      currencyCode = tempDataCurrencyCode || selectedPlan.rate.currency.currencyCode;
      console.log('SummaryStep: Using selectedPlan data (prioritized for new customers):', tempDataRateAmount, originalPremiumAmount, currencyCode);
    }
    // Priority 3: Use current tempData (fallback for existing customers from email verification)
    else if (tempDataRateAmount !== null &&
             tempDataRateAmount !== undefined &&
             typeof tempDataRateAmount === 'number' &&
             tempDataRateAmount > 0 &&
             !isNaN(tempDataRateAmount) &&
             tempDataCurrencyCode &&
             typeof tempDataCurrencyCode === 'string' &&
             tempDataCurrencyCode.trim() !== '') {
      originalPremiumAmount = tempDataRateAmount;
      currencyCode = tempDataCurrencyCode;
      console.log('SummaryStep: Using tempData rateAmount and currencyCode (fallback for existing customers):', originalPremiumAmount, currencyCode);
    } else {
      console.log('SummaryStep: No valid premium data found - Debug info:', {
        isForexConverted,
        convertedRateAmount,
        convertedCurrencyCode,
        selectedPlanRateAmount: selectedPlan?.rate?.rateAmount,
        selectedPlanCurrencyCode: selectedPlan?.rate?.currency?.currencyCode,
        tempDataRateAmount,
        tempDataCurrencyCode
      });
    }

    // Enhanced fallback logic for incomplete tempData scenarios
    if (!selectedPlan && !tempDataProductName) {
      console.log('SummaryStep: No plan data available from either selectedPlan or tempData');
      
      // Check if we have ANY tempData at all (even if incomplete)
      const allTempDataKeys = tempData && typeof tempData === 'object' ? Object.keys(tempData as Record<string, unknown>) : [];
      const hasSomeTempData = allTempDataKeys.length > 0;
      
      console.log('SummaryStep: Checking for any tempData:', { hasSomeTempData, keys: allTempDataKeys });
      
      if (hasSomeTempData) {
        // We have tempData but it's missing pricing info - this is a backend data issue
        console.log('SummaryStep: TempData exists but missing pricing information');
        
        // Try to extract what we can from tempData
        const tempDataPlanType = getTempDataValue('planType') as number;
        const tempDataTypeName = getTempDataValue('typeName') as string;
        const tempDataStartTrip = getTempDataValue('startTrip') as string;
        const tempDataDestinations = getTempDataValue('destinations') as string;
        
        // Map planType number to name
        let planTypeName = planType;
        if (tempDataPlanType === 1) planTypeName = 'Individual';
        else if (tempDataPlanType === 2) planTypeName = 'Family';
        else if (tempDataPlanType === 3) planTypeName = 'Group';
        
        const displayPlanType = tempDataTypeName || planTypeName;
        const travelInfo = tempDataStartTrip && tempDataDestinations
          ? `${tempDataStartTrip} to ${tempDataDestinations}`
          : 'Travel Insurance';
        
        return {
          planName: `${displayPlanType} Plan`,
          productType: 'Travelsafe',
          typeOfPlan: displayPlanType,
          plan: `${displayPlanType} - ${travelInfo}`,
          medicalTreatmentLimit: 'Please contact support for plan details',
          originalPremium: 'Pricing information unavailable - Please contact support',
          paymentPremium: 'Please contact support for pricing'
        };
      }
      
      // Check if we have partial tempData with pricing
      const hasPartialTempData = tempDataRateAmount || tempDataCurrencyCode ||
                                getTempDataValue('productName') ||
                                getTempDataValue('typeName');
      
      if (hasPartialTempData) {
        console.log('SummaryStep: Found partial tempData, attempting to construct plan info');
        const fallbackProductName = getTempDataValue('productName') as string ||
                                   getTempDataValue('typeName') as string ||
                                   'Travel Insurance';
        const fallbackCurrency = tempDataCurrencyCode || 'PHP';
        const fallbackAmount = tempDataRateAmount || 0;
        
        return {
          planName: `${fallbackProductName} ${planType}`,
          productType: 'Travelsafe',
          typeOfPlan: planType,
          plan: `${fallbackProductName} ${planType}`,
          medicalTreatmentLimit: 'Contact support for details',
          originalPremium: fallbackAmount > 0 ? `${fallbackCurrency} ${fallbackAmount.toLocaleString()}` : 'Contact support for pricing',
          paymentPremium: fallbackAmount > 0 ? `${fallbackCurrency} ${fallbackAmount.toLocaleString()}` : 'Contact support for pricing'
        };
      }
      
      return {
        planName: 'No Data Available',
        productType: 'Travelsafe',
        typeOfPlan: planType,
        plan: 'No Data Available',
        medicalTreatmentLimit: 'No Data, to be added',
        originalPremium: 'No Data Available',
        paymentPremium: 'No Data Available'
      };
    }

    // Prioritize selectedPlan data over tempData for product information (better for new customers)
    const productName = selectedPlan?.productName || tempDataProductName || 'Unknown';
    const planTypeName = selectedPlan?.planType || tempPlanDetails?.typeName || planType;
    
    // Enhanced fallback handling - if we have selectedPlan but no premium data, try to use selectedPlan's rate
    if (!originalPremiumAmount || !currencyCode) {
      // Last attempt: try to extract from selectedPlan even if validation failed earlier
      if (selectedPlan?.rate?.rateAmount && selectedPlan?.rate?.currency?.currencyCode) {
        originalPremiumAmount = selectedPlan.rate.rateAmount;
        currencyCode = selectedPlan.rate.currency.currencyCode;
        console.log('SummaryStep: Using selectedPlan data as final fallback:', originalPremiumAmount, currencyCode);
      } else {
        console.log('SummaryStep: No premium data available, showing plan info without pricing');
        return {
          planName: `${productName} ${planType}`,
          productType: 'Travelsafe',
          typeOfPlan: planType,
          plan: `${productName} ${planTypeName}`,
          medicalTreatmentLimit: selectedPlan?.maxBenefitLimit ?
            `${selectedPlan.rate?.currency?.currencyCode || 'PHP'} ${selectedPlan.maxBenefitLimit.toLocaleString()}` :
            'No Data, to be added',
          originalPremium: 'No Data Available',
          paymentPremium: 'No Data Available'
        };
      }
    }
    
    let originalPremium: string;
    let paymentPremium: string;

    if (isGroupPlan) {
      // Group plan calculation
      const groupMemberCount = groupMembers.length;
      const totalOriginalPremium = originalPremiumAmount * groupMemberCount;
      originalPremium = `${currencyCode} ${totalOriginalPremium.toLocaleString()}`;

      // Calculate payment premium with age adjustments using the correct two-formula approach
      let totalPaymentPremium = 0;
      let hasAnyAdjustments = false;
      let membersWithAdjustments = 0;
      let membersWithoutAdjustments = 0;
      
      // Check if we have any premium adjustments (context takes priority over props)
      const activeAdjustments = contextPremiumAdjustments?.groupMemberAdjustments ||
                               propPremiumAdjustments?.groupMemberAdjustments ||
                               [];
      
      // Debug logging for premium adjustments
      console.log('SummaryStep: Group Plan Premium Calculation Debug:');
      console.log('- contextPremiumAdjustments:', contextPremiumAdjustments);
      console.log('- propPremiumAdjustments:', propPremiumAdjustments);
      console.log('- activeAdjustments:', activeAdjustments);
      console.log('- originalPremiumAmount:', originalPremiumAmount);
      console.log('- groupMembers count:', groupMembers.length);
      
      // Categorize members and calculate totals
      groupMembers.forEach(member => {
        const memberAge = parseInt(member.age);
        
        // Look for adjustment by member ID or by temporary ID pattern
        let adjustment = activeAdjustments.find(adj => adj.memberId === member.id);
        
        // If no adjustment found by member ID, try to find by temporary ID pattern
        if (!adjustment && memberAge >= 76 && memberAge <= 100) {
          const tempMemberId = `temp-${member.firstName}-${member.lastName}-${memberAge}`;
          adjustment = activeAdjustments.find(adj => adj.memberId === tempMemberId);
        }
        
        console.log(`SummaryStep: Processing member ${member.id} (${member.firstName} ${member.lastName}), age: ${memberAge}`);
        console.log(`- Found adjustment:`, adjustment);
        
        // Check if member is in age range 76-100 AND has an actual adjustment
        if (memberAge >= 76 && memberAge <= 100 && adjustment) {
          // Member has premium adjustment - use the adjusted price
          totalPaymentPremium += adjustment.adjustedPrice;
          membersWithAdjustments++;
          hasAnyAdjustments = true;
          console.log(`- Using adjusted price: ${adjustment.adjustedPrice} (original: ${originalPremiumAmount})`);
        } else {
          // Member uses original premium amount
          totalPaymentPremium += originalPremiumAmount;
          membersWithoutAdjustments++;
          console.log(`- Using original price: ${originalPremiumAmount}`);
        }
      });
      
      // Apply the correct formula based on whether adjustments exist
      if (hasAnyAdjustments) {
        // First Formula: With Premium Adjustment
        // (members with adjusted price × adjustedPrice) + (members without adjustment × currentPremium.amount)
        console.log(`SummaryStep: Group Plan Payment Premium - WITH adjustments`);
        console.log(`- Members with adjustments: ${membersWithAdjustments}`);
        console.log(`- Members without adjustments: ${membersWithoutAdjustments}`);
        console.log(`- Total Payment Premium: ${totalPaymentPremium}`);
        paymentPremium = `${currencyCode} ${totalPaymentPremium.toLocaleString()}`;
      } else {
        // Second Formula: Without adjustment - same as Original Premium
        console.log(`SummaryStep: Group Plan Payment Premium - WITHOUT adjustments (same as Original Premium)`);
        totalPaymentPremium = totalOriginalPremium;
        paymentPremium = originalPremium;
      }
    } else {
      // Individual/Family/Multi-trip plan calculation
      originalPremium = `${currencyCode} ${originalPremiumAmount.toLocaleString()}`;
      
      // Check for principal insured age adjustment
      // First priority: use premiumAdjustments from context
      if (contextPremiumAdjustments?.principalInsuredAdjustment) {
        console.log('SummaryStep: Using principalInsuredAdjustment from context:', contextPremiumAdjustments.principalInsuredAdjustment);
        paymentPremium = `${currencyCode} ${contextPremiumAdjustments.principalInsuredAdjustment.adjustedPrice.toLocaleString()}`;
      }
      // Second priority: use premiumAdjustments prop if provided (for backward compatibility)
      else if (propPremiumAdjustments?.principalInsuredAdjustment) {
        console.log('SummaryStep: Using principalInsuredAdjustment from props:', propPremiumAdjustments.principalInsuredAdjustment);
        paymentPremium = `${currencyCode} ${propPremiumAdjustments.principalInsuredAdjustment.adjustedPrice.toLocaleString()}`;
      }
      // Third priority: use adjustedPrice from the hook if available
      else if (adjustedPrice && adjustedPrice > 0) {
        console.log('SummaryStep: Using adjustedPrice from hook:', adjustedPrice);
        paymentPremium = `${currencyCode} ${adjustedPrice.toLocaleString()}`;
      }
      // Fourth priority: use original premium (no age adjustment)
      else {
        console.log('SummaryStep: Using original premium, no adjustment found');
        paymentPremium = originalPremium;
      }
    }

    // IMPORTANT: Medical Treatment limits should preserve original currency (EUR) - NOT converted currency
    // This is per user requirement: "do not change the Medical Treatment or the mbl currency code if it's EURO"
    let medicalTreatmentLimit = 'No Data, to be added';
    
    if (selectedPlan?.benefitsCategories) {
      const medicalBenefit = selectedPlan.benefitsCategories.find(b => b.benefitsCategory === 'Medical Treatment');
      // Use original plan currency for medical benefits, not converted currency
      const originalPlanCurrency = selectedPlan.rate.currency.currencyCode;
      medicalTreatmentLimit = medicalBenefit?.categoryCoverage
        ? `${originalPlanCurrency} ${medicalBenefit.categoryCoverage}`
        : `${originalPlanCurrency} ${selectedPlan.maxBenefitLimit.toLocaleString()}`;
      console.log('SummaryStep: Using original plan currency for medical treatment limit:', medicalTreatmentLimit);
    } else {
      // Fallback to tempData "mbl" - use original currency if forex conversion occurred
      const tempDataMbl = getTempDataValue('mbl') as string | number | null;
      
      if (tempDataMbl) {
        // Use original currency for medical benefits if forex conversion occurred
        const medicalCurrency = isForexConverted && originalCurrencyCode ? originalCurrencyCode : currencyCode;
        
        // Convert to number if it's a string, then format with currency
        const mblValue = typeof tempDataMbl === 'string' ? parseFloat(tempDataMbl) : tempDataMbl;
        if (!isNaN(mblValue) && mblValue > 0) {
          medicalTreatmentLimit = `${medicalCurrency} ${mblValue.toLocaleString()}`;
          console.log('SummaryStep: Using tempData mbl with original currency for medical treatment limit:', medicalTreatmentLimit);
        }
      }
    }

    return {
      planName: `${productName} ${planType}`,
      productType: 'Travelsafe',
      typeOfPlan: planType,
      plan: `${productName} ${planTypeName}`,
      medicalTreatmentLimit,
      originalPremium,
      paymentPremium
    };
  }, [selectedPlan, planData, isGroupPlan, isFamilyPlan, travelDetailsData, groupMembers, contextPremiumAdjustments, propPremiumAdjustments, data.principalinsured, adjustedPrice, getTempDataValue]);

  // Helper function to calculate age from date of birth string
  const calculateAgeFromDateString = (dateOfBirth: string): string => {
    if (!dateOfBirth) return '0';
    
    const birthDate = dateUtils.parseDate(dateOfBirth);
    if (!birthDate) return '0';
    
    const age = dateUtils.calculateAge(birthDate);
    return age.toString();
  };

  // Create insured persons array for display
  const createInsuredPersons = () => {
    const insuredPersons: InsuredPerson[] = [];
    
    // For Group Plan, use group members data
    const groupMembers = data.groupinformation?.groupMembers || [];
    if (groupMembers.length > 0) {
      groupMembers.forEach((member) => {
        // Calculate age from dateOfBirth if age is not provided or seems incorrect
        const calculatedAge = calculateAgeFromDateString(member.dateOfBirth);
        const displayAge = member.age && member.age !== '0' ? member.age : calculatedAge;
        
        insuredPersons.push({
          id: member.id,
          type: 'Group Member',
          title: member.title,
          firstName: member.firstName,
          middleName: member.middleName,
          lastName: member.lastName,
          suffix: member.suffix,
          dateOfBirth: member.dateOfBirth,
          age: displayAge,
          gender: member.gender,
          occupation: member.occupation,
          nationality: member.nationality,
          civilStatus: member.civilStatus,
          address: member.address || formatAddressForSummary({
            // houseUnitNo: member.houseUnitNo,
            streetName: member.streetName,
            subdivisionVillage: member.subdivisionVillage,
            barangay: member.barangay,
            city: member.city,
            province: member.province,
            zipCode: member.zipCode,
            country: member.country
          }),
          contactNumber: member.formattedPhoneNumber || member.contactNumber,
          idType: member.idType,
          idNumber: member.idNumber,
          beneficiaries: member.beneficiaries || []
        });
      });
      return insuredPersons;
    }
    
    // Always add Principal Insured first (for Individual/Family plans)
    if (principalInsured) {
      // Calculate age from dateOfBirth
      const calculatedAge = calculateAgeFromDateString(principalInsured.dateOfBirth);
      
      insuredPersons.push({
        id: 'principal',
        type: 'Principal Insured',
        title: principalInsured.title,
        firstName: principalInsured.firstName,
        middleName: principalInsured.middleName,
        lastName: principalInsured.lastName,
        suffix: principalInsured.suffix,
        dateOfBirth: principalInsured.dateOfBirth,
        age: calculatedAge,
        occupation: principalInsured.occupation,
        nationality: principalInsured.nationality,
        civilStatus: principalInsured.civilStatus,
        address: data.homeaddress ?
          formatAddressForSummary(data.homeaddress) :
          'Legrand tower 1, Quezon City, Metro Manila, Philippines',
        contactNumber: data.contactinformation?.formattedPhoneNumber,
        idNumber: principalInsured.idNumber,
        idType: principalInsured.idType
      });
    }
    
    // Add spouse if exists (for Family Plan)
    if (spouse) {
      // Calculate age from dateOfBirth if age is not provided or seems incorrect
      const calculatedAge = calculateAgeFromDateString(spouse.dateOfBirth);
      const displayAge = spouse.age && spouse.age !== '0' ? spouse.age : calculatedAge;
      
      insuredPersons.push({
        id: 'spouse',
        type: 'Spouse',
        title: spouse.title,
        firstName: spouse.firstName,
        middleName: spouse.middleName,
        lastName: spouse.lastName,
        suffix: spouse.suffix,
        dateOfBirth: spouse.dateOfBirth,
        age: displayAge,
        gender: spouse.gender,
        nationality: spouse.nationality || 'Not specified',
        address: formatAddressForSummary({
          // houseUnitNo: spouse.houseUnitNo,
          streetName: spouse.streetName,
          subdivisionVillage: spouse.subdivisionVillage,
          barangay: spouse.barangay,
          city: spouse.city,
          province: spouse.province,
          zipCode: spouse.zipCode,
          country: spouse.country
        }),
        idType: spouse.idType,
        idNumber: spouse.idNumber
      });
    }
    
    // Add children if exist (for Family Plan)
    children.forEach((child, index) => {
      // Calculate age from dateOfBirth if age is not provided or seems incorrect
      const calculatedAge = calculateAgeFromDateString(child.dateOfBirth);
      const displayAge = child.age && child.age !== '0' ? child.age : calculatedAge;
      
      insuredPersons.push({
        id: `child-${index}`,
        type: 'Child',
        title: child.title,
        firstName: child.firstName,
        middleName: child.middleName,
        lastName: child.lastName,
        suffix: child.suffix,
        dateOfBirth: child.dateOfBirth,
        age: displayAge,
        gender: child.gender,
        nationality: '', // Remove hardcoded nationality for children
        address: formatAddressForSummary({
          // houseUnitNo: child.houseUnitNo,
          streetName: child.streetName,
          subdivisionVillage: child.subdivisionVillage,
          barangay: child.barangay,
          city: child.city,
          province: child.province,
          zipCode: child.zipCode,
          country: child.country
        })
      });
    });
    
    return insuredPersons;
  };


  const insuredPersons = createInsuredPersons();
  
  // Calculate pagination data for insured persons
  const paginationData = calculatePaginationData(insuredPersons);

  return (
    <div className="summary-step">
      <div className="summary-content">
        {/* Plan Details Section */}
        <div className="summary-section plan-details-section">
          <div className="summary-section-header">
            <h3 className="summary-section-title">Plan Details</h3>
          </div>
          <div className="section-content">
            {enhancedPlanData ? (
              <>
                <div className="plan-header-with-edit">
                  <div className="plan-header-content">
                    <span className="plan-name">{enhancedPlanData.planName}</span>
                    <span className="plan-label">Plan</span>
                  </div>
                </div>
                
                <div className="plan-details-table">
                  <div className="detail-row">
                    <span className="detail-label">Product Type</span>
                    <span className="detail-value">{enhancedPlanData.productType}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type of Plan</span>
                    <span className="detail-value">{enhancedPlanData.typeOfPlan}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Plan</span>
                    <span className="detail-value">{enhancedPlanData.plan}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Medical Treatment</span>
                    <span className="detail-value">{enhancedPlanData.medicalTreatmentLimit}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Original Premium</span>
                    <span className="detail-value">{originalPremiumAmountStr}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Payment Premium</span>
                    <span className="detail-value">{paymentPremiumAmountStr}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-plan-data">
                <span className="no-data-text">Plan details not available</span>
              </div>
            )}
          </div>
        </div>

        {/* Travel Details Section */}
        <div className="summary-section travel-details-section">
          <div className="summary-section-header">
            <h3 className="summary-section-title">Travel Details</h3>
          </div>
          <div
            className="summary-section-header expandable"
            onClick={() => toggleSection('travelDetails')}
          >
            <div className="summary-section-header-content-with-edit">
              <div className="section-info">
                <span className="section-main-text">{enhancedTravelData.itinerary}</span>
                <span className="section-sub-text">Itinerary</span>
              </div>

            </div>
            <AnimatedChevron
              isExpanded={expandedSections.travelDetails}
              className="section-chevron"
              size={20}
            />
          </div>
          
          {expandedSections.travelDetails && (
            <div className="section-content expanded-content">
              <div className="expanded-details-table">
                {/* Schengen Coverage - Only for Worldwide Elite plans */}
                {isWorldwideElitePlan && (
                  <div className="detail-row">
                    <span className="detail-label">Schengen Coverage</span>
                    <span className="detail-value">Yes</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Coverage Area</span>
                  <span className="detail-value">{enhancedTravelData.coverageArea}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Coverage Period</span>
                  <span className="detail-value">{enhancedTravelData.coveragePeriod}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Insured Person/s Section - Multiple expandable sections with pagination */}
        <div className="summary-section insured-persons-section">
          <div className="summary-section-header-content-with-edit">
            <div className="section-info">
              <h3 className="summary-section-title">Insured Person/s</h3>
            </div>
          </div>
          
          {/* Display current page items */}
          {paginationData.currentPageItems.map((person) => (
            <div key={person.id}>
              <div
                className="summary-section-header expandable"
                onClick={() => toggleSection(`insured-${person.id}`)}
              >
                <div className="summary-section-header-content">
                  <div className="section-info">
                    <span className="section-main-text">
                      {formatFullName(person)}
                    </span>
                    <span className="section-sub-text">{person.type}</span>
                  </div>              
                </div>
                <AnimatedChevron
                  isExpanded={expandedSections[`insured-${person.id}`] || false}
                  className="section-chevron"
                  size={20}
                />                
              </div>
              
              {expandedSections[`insured-${person.id}`] && (
                <div className="section-content expanded-content">
                  <div className="expanded-details-table">
                    <div className="detail-row">
                      <span className="detail-label">Birthdate</span>
                      <span className="detail-value">{person.dateOfBirth}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Current Age</span>
                      <span className="detail-value">{person.age}</span>
                    </div>
                    {person.type === 'Principal Insured' && (
                      <>
                        <div className="detail-row">
                          <span className="detail-label">Occupation</span>
                          <span className="detail-value">{person.occupation}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Civil Status</span>
                          <span className="detail-value">{person.civilStatus}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Contact Number</span>
                          <span className="detail-value">{person.contactNumber}</span>
                        </div>
                      </>
                    )}
                    {person.type === 'Group Member' && (
                      <>
                        <div className="detail-row">
                          <span className="detail-label">Occupation</span>
                          <span className="detail-value">{person.occupation}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Civil Status</span>
                          <span className="detail-value">{person.civilStatus}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Contact Number</span>
                          <span className="detail-value">{person.contactNumber}</span>
                        </div>
                      </>
                    )}
                    {person.gender && (
                      <div className="detail-row">
                        <span className="detail-label">Gender</span>
                        <span className="detail-value">{person.gender}</span>
                      </div>
                    )}
                    {person.type !== 'Child' && (
                      <div className="detail-row">
                        <span className="detail-label">Nationality</span>
                        <span className="detail-value">{person.nationality}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Address</span>
                      <span className="detail-value">{person.address}</span>
                    </div>
                    {person.idType && (
                      <>
                        <div className="detail-row">
                          <span className="detail-label">Type of ID</span>
                          <span className="detail-value">{person.idType}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">ID Number</span>
                          <span className="detail-value">{person.idNumber}</span>
                        </div>
                      </>
                    )}
                    
                    {/* Beneficiary information for Group Members */}
                    {person.beneficiaries &&
                      person.beneficiaries.length > 0 && (
                        <div className="detail-row">
                          <span className="detail-label">Beneficiary</span>
                          <span className="detail-value">
                            {person.beneficiaries.map((beneficiary, index) => (
                              <span
                                key={beneficiary.id}
                                className="beneficiary-item group-plan-beneficiary-item-summary"
                              >
                                {formatFullName(beneficiary)} -{" "}
                                {beneficiary.relationship}
                                {index < person.beneficiaries!.length - 1 && (
                                  <br />
                                )}
                              </span>
                            ))}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {/* Pagination Component */}
          {paginationData.totalPages > 1 && (
            <Pagination
              currentPage={paginationData.currentPage}
              totalPages={paginationData.totalPages}
              onPageChange={handleInsuredPersonsPageChange}
              className="insured-persons-pagination"
            />
          )}
        </div>

        {/* Family Members Section - Only for Family Plan */}
        {familyMembers.length > 0 && (
          <div className="summary-section family-members-section">
            <h3 className="summary-section-title">Family Members</h3>
            <div className="section-content">
              {familyMembers.map((member, index) => (
                <div key={member.id || index} className="family-member-item">
                  <div className="member-info">
                    <span className="member-name">
                      {formatFullName(member)}
                    </span>
                    <span className="member-relationship">{member.relationship}</span>
                  </div>
                  <div className="member-details">
                    <span className="member-detail">Born: {member.dateOfBirth}</span>
                    <span className="member-detail">{member.gender}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Beneficiary Section - Only for Individual and Family Plans */}
        {!isGroupPlan && (
          <div className="summary-section-beneficiary beneficiary-section">
            <div className="summary-section-header">
              <h3 className="summary-section-title">Beneficiary</h3>
            </div>
            <div className="section-content-beneficiary">
              {beneficiaries.length > 0 ? (
                beneficiaries.map((beneficiary, index) => (
                  <div key={beneficiary.id || index} className="summary-beneficiary-item">
                    <div className="section-info">
                      <span className="beneficiary-name">
                        {formatFullName(beneficiary)}
                      </span>
                      <span className="beneficiary-relationship">{beneficiary.relationship}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-beneficiaries">
                  <span className="no-data-text">No beneficiaries added</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Personal Accident Beneficiary Section - Only for Family Plan */}
        {personalAccidentBeneficiaries.length > 0 && (
          <div className="summary-section personal-accident-beneficiary-section">
            <h3 className="summary-section-title">Personal Accident Beneficiary</h3>
            <div className="section-content">
              {personalAccidentBeneficiaries.map((beneficiary, index) => (
                <div key={beneficiary.id || index} className="pa-beneficiary-item">
                  <span className="pa-beneficiary-name">
                    {formatFullName(beneficiary)}
                  </span>
                  <span className="pa-beneficiary-relationship">
                    {beneficiary.relationship}
                  </span>
                  <span className="pa-beneficiary-relationship">{beneficiary.relationship}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Terms and Conditions Section */}
        <div className="summary-section terms-section">
          <h3 className="summary-section-title">Terms and Conditions</h3>
          <div className="section-content">
          <p className="terms-intro">
            To continue, please read the terms below and tick the box at the bottom to confirm you understand and agree.
          </p>
            <div className="terms-checkboxes">
              {/* First Checkbox - Privacy Statement */}
              <label className="summary-checkbox-item">
                <input
                  className='summary-checkbox-dimensions'
                  type="checkbox"
                  checked={termsAccepted.privacyStatement}
                  onChange={() => handleTermsChange('privacyStatement')}
                />
                <span className="summary-checkbox-text">
                  I (we) represent and warrant that I am (we are) authorized to use this web form, and that I (we)
                  have read and understood, and agree to the{' '}
                  <span
                    className="terms-link"
                    onClick={() => handleLinkClick('privacy-statement')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLinkClick('privacy-statement')}
                  >
                    Pacific Cross Privacy Statement
                  </span>
                  . I (we) further authorize Pacific Cross to use my (our) personal data in accordance with such Privacy Statement,
                  the Data Privacy Act of 2021, and its implementing rules and regulations.
                </span>
              </label>

              {/* Second Checkbox - Application Terms */}
              <label className="summary-checkbox-item">
                <input
                  className='summary-checkbox-dimensions'
                  type="checkbox"
                  checked={termsAccepted.applicationTerms}
                  onChange={() => handleTermsChange('applicationTerms')}
                />
                <span className="summary-checkbox-text">
                  I have read and understood the{' '}
                  <span
                    className="terms-link"
                    onClick={() => handleLinkClick('application-terms')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLinkClick('application-terms')}
                  >
                    Application Terms and Conditions
                  </span>
                  ,{' '}
                  <span
                    className="terms-link"
                    onClick={() => handleLinkClick('exclusions')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLinkClick('exclusions')}
                  >
                    Exclusions
                  </span>
                  ,{' '}
                  <span
                    className="terms-link"
                    onClick={() => handleLinkClick('important-notes')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLinkClick('important-notes')}
                  >
                    Important Notes
                  </span>
                  {' '}(Applicable to Family Plans only),{' '}
                  <span
                    className="terms-link"
                    onClick={() => handleLinkClick('terms-defined')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLinkClick('terms-defined')}
                  >
                    Some Terms Defined and Other Important Details
                  </span>
                  {' '}of Pacific Cross Travelsafe Insurance, and this site's{' '}
                  <span
                    className="terms-link"
                    onClick={() => handleLinkClick('payment-terms')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLinkClick('payment-terms')}
                  >
                    Payment Terms and Conditions
                  </span>
                  .
                </span>
              </label>

              {/* Third Checkbox - Marketing Consent (Optional) */}
              <label className="summary-checkbox-item">
                <input
                  className='summary-checkbox-dimensions'
                  type="checkbox"
                  checked={termsAccepted.marketingConsent}
                  onChange={() => handleTermsChange('marketingConsent')}
                />
                <span className="summary-checkbox-text">
                  I want to receive information and updates on Pacific Cross's products and services.
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Error Display */}
      {transactionError && (
        <div className="transaction-error" style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          padding: '12px',
          margin: '16px 0',
          color: '#c33'
        }}>
          <strong>Transaction Error:</strong> {transactionError}
        </div>
      )}

      {/* Action Buttons */}
      <div className="summary-actions">
        <button
          type="button"
          className="btn-edit-details flex items-center justify-center"
          onClick={handleEditDetails}
        >
          Back
        </button>
        <button
          type="button"
          className="btn-pay flex items-center justify-center"
          onClick={() => {
             setShowRecaptchaModal(true);
          }}
          disabled={!allTermsAccepted || transactionLoading}
        >
          {transactionLoading ? 'Processing...' : 'Pay'}
        </button>
      </div>
      <PaymentTransactionModal
        isOpen={paymentModal.isOpen}
        onClose={() => setPaymentModal(prev => ({ ...prev, isOpen: false }))}
        type={paymentModal.type}
        travelDetails={paymentModal.travelDetails}
      />
      <RecaptchaModal
        isOpen={showRecaptchaModal}
        onClose={(isValidated?: boolean) => {
          if (isValidated) {
            setShowRecaptchaModal(false);
            handlePay();
          } else {
            setShowRecaptchaModal(false);
          }
        }}
      />
    </div>
  );
};

export default SummaryStep;
